import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getCountryCurrency } from '../utils/countryCurrency';
import OutletSelectionModal from './OutletSelectionModal';

export type PlanId = 'basic' | 'pro' | 'ultra';
export type BillingCycle = 'monthly' | 'yearly';
export type ModuleId = 'ledger' | 'employees' | 'queue' | 'gym' | 'canteen' | 'pos';
export type ModuleStatus = 'inactive' | 'trial' | 'active' | 'locked';

export interface PlanFeature {
  label: string;
  basic: string | boolean;
  pro: string | boolean;
  ultra: string | boolean;
  highlight?: boolean;
}

// ── Individual Suite pricing (UNCHANGED — this is the only suite now) ──────
const DEFAULT_PRICES: Record<PlanId, { monthly: number; yearly: number; monthlyCurrency: string; yearlyCurrency: string }> = {
  basic:  { monthly: 0, yearly: 0, monthlyCurrency: '', yearlyCurrency: '' },
  pro:    { monthly: 0, yearly: 0, monthlyCurrency: '', yearlyCurrency: '' },
  ultra:  { monthly: 0, yearly: 0, monthlyCurrency: '', yearlyCurrency: '' },
};

const SEAT_LIMITS: Record<PlanId, string> = {
  basic: '1 user · 5 invoices/mo',
  pro:   '1 user · 30 invoices/mo',
  ultra: '1 user · unlimited invoices',
};

const BASIC_TAGLINE = 'Free forever · 5 invoices/month';

// ── Module definitions ──────────────────────────────────────────────────────
// id matches MODULE_PLANS in backend router.py exactly — no suffixes.
// NOTE: prices below are sane starting defaults, not the old $400 placeholder —
// tune these once you've settled on final INR anchor pricing on the backend.
interface ModuleDef {
  id: ModuleId;
  icon: string;
  name: string;
  desc: string;
  monthlyPrice: number;
  monthlyCurrency: string;
  features: string[];
}

const MODULE_DEFS: ModuleDef[] = [
  {
    id: 'ledger',
    icon: '📒',
    name: 'Credit Book',
    monthlyPrice: 0,
    monthlyCurrency: '',
    desc: 'Accounts Receivable (AR) & Udhar Khata — customer credit management',
    features: [
      '✅ Accounts Receivable (AR) & credit ledger',
      '✅ Unlimited customer credit accounts',
      '✅ Recurring charges & automated settlement engine',
      '✅ Smart QR — touchless customer payments & charge entry',
      '✅ Instant payment link generation & WhatsApp reminders',
      '✅ Multi-currency transactions & global payment gateways (30+)',
      '✅ Custom billing categories & balance threshold alerts',
      '✅ Promotional broadcast messaging & customer outreach',
      '✅ Role-based access control & IAM permissions',
      '✅ 1 outlet, 2 seats included',
    ],
  },
  {
    id: 'employees',
    icon: '🕐',
    name: 'Staff & Attendance',
    monthlyPrice: 12,
    monthlyCurrency: 'USD',
    desc: 'Human Resources (HR), biometric attendance & payroll automation',
    features: [
      '✅ Human Resources (HR) & staff profile management',
      '✅ Up to 50 employees per branch outlet',
      '✅ Employee self-service portal (attendance, leaves & payslips)',
      '✅ Face Scan + Fingerprint + QR kiosk check-in',
      '✅ ZKTeco biometric hardware support',
      '✅ Automated payroll & salary engine calculated from attendance',
      '✅ Leave lifecycle management (apply, approve & WhatsApp decline reasons)',
      '✅ Automated WhatsApp & Email attendance alerts',
      '✅ QR badge scan logs & exportable payroll reports',
      '✅ Multi-currency & OTP-verified identity security',
      '✅ 1 outlet, 2 seats included',
    ],
  },
  {
    id: 'queue',
    icon: '🎫',
    name: 'Queue / Tokens',
  monthlyPrice: 0,
    monthlyCurrency: '',
    desc: 'Token dispatch system, multi-display screens & multilingual voice calling',
    features: [
      '✅ Token dispatch system (walk-in & appointment scheduling)',
      '✅ Up to 6 service counters, display screens & desk links per outlet',
      '✅ Multilingual voice announcements (100+ languages, male/female)',
      '✅ Instant custom text-to-speech announcement engine',
      '✅ Live waiting-room display screens (combined or per-counter)',
      '✅ Customer live tracking links via WhatsApp, Email, or QR scan',
      '✅ Bilingual printed token slips (thermal, card, classic)',
      '✅ Customer CRM auto-synced from token registrations',
      '✅ Permanent operational audit logs & IAM permissions',
      '✅ 1 outlet, 2 seats included',
    ],
  },
  {
    id: 'gym',
    icon: '🏋️',
    name: 'Fitness Center',
    monthlyPrice: 0,
    monthlyCurrency: '',
    desc: 'Facility memberships, recurring billing & biometric access',
    features: [
      '✅ Unlimited active members & subscription tracking',
      '✅ Membership plan creation & recurring renewals',
      '✅ Biometric access (Face scan, Fingerprint & QR badges)',
      '✅ Touchless self-service check-in kiosk',
      '✅ Real-time membership status dashboard (expiring/expired)',
      '✅ Automated renewal reminders via WhatsApp & Email',
      '✅ Integrated payment links & 30+ payment gateways',
      '✅ Client CRM, attendance reports & promo broadcasts',
      '✅ 1 outlet, 2 seats included',
    ],
  },
  {
    id: 'canteen',
    icon: '🍽️',
    name: 'Meal Plans & Dining',
    monthlyPrice: 0,
    monthlyCurrency: '',
    desc: 'Food subscriptions, dining slots & meal collection verification',
    features: [
      '✅ Unlimited members & meal plan subscriptions',
      '✅ Multi-slot meal management (Breakfast, Lunch, Dinner tracking)',
      '✅ Real-time order fulfillment & collection tracking',
      '✅ Biometric verification (QR badge, Face scan & Fingerprint)',
      '✅ Self-service dining kiosk setup',
      '✅ Automated renewal reminders via WhatsApp & Email',
      '✅ Integrated payment links & 30+ payment gateways',
      '✅ Member meal history & exportable consumption reports',
      '✅ 1 outlet, 2 seats included',
    ],
  },
  {
    id: 'pos',
    icon: '🧾',
    name: 'POS / Billing',
    monthlyPrice: 0,
    monthlyCurrency: '',
    desc: 'Retail terminal, master inventory, orders & table management',
    features: [
      '✅ Unlimited sales & rapid checkout terminal',
      '✅ Barcode & QR code scanning engine',
      '✅ Master product catalog & barcode label printing',
      '✅ Real-time inventory tracking & low stock alerts',
      '✅ Supplier management & purchase order records',
      '✅ Kitchen Display System (KDS) & order token sync',
      '✅ Customer live order tracking QR (auto-notify when ready)',
      '✅ Dual display & customer-facing payment screen',
      '✅ 200+ invoice templates & thermal receipt printing (58mm/80mm/A4)',
      '✅ Bilingual receipts (100+ languages) & multi-currency rules',
      '✅ Cash, Card, UPI + 30+ international payment gateways',
      '✅ 1 outlet, 2 seats included',
    ],
  },
];

// Modules where the org's first outlet is free — must mirror
// FREE_FIRST_OUTLET_MODULES in billing/router.py.
const FREE_FIRST_OUTLET_MODULES = new Set(['ledger']);
function billableOutlets(modId: string, count: number, freeSlotUsed: boolean): number {
  if (FREE_FIRST_OUTLET_MODULES.has(modId) && !freeSlotUsed) return Math.max(0, count - 1);
  return count;
}

// ── Shared helpers ──────────────────────────────────────────────────────────
const PLAN_META: Record<PlanId, { name: string; color: string; accent: string }> = {
  basic: { name: 'Basic', color: '#64748b', accent: '#f1f5f9' },
  pro:   { name: 'Pro',   color: '#6366f1', accent: '#eef2ff' },
  ultra: { name: 'Ultra', color: '#7c3aed', accent: '#f5f3ff' },
};

const BASIC_INVOICE_LIMIT = '5 invoices/mo';

function CheckIcon({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="8" fill={color} fillOpacity="0.12" />
      <path d="M5 8.5l2 2 4-4" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M4 4l6 6M10 4l-6 6" stroke="#cbd5e1" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function FeatureCell({ value, planColor }: { value: string | boolean; planColor: string }) {
  if (value === true)  return <CheckIcon color={planColor} />;
  if (value === false) return <XIcon />;
  return <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{value}</span>;
}

// ── Module Card ─────────────────────────────────────────────────────────────
// Driven purely by ModuleStatus from backend — no Full Suite props involved

function getStatusMeta(symbol: string, price: number): Record<ModuleStatus, { label: string; sub: string; color: string }> {
  return {
    inactive: { label: '🎁 Start 7-day Free Trial', sub: 'No card needed',                                      color: '#6366f1' },
    trial:    { label: `💳 Subscribe — ${symbol}${price}/mo`, sub: 'Full access · pay anytime to keep it',       color: '#6366f1' },
    locked:   { label: `💳 Renew Access — ${symbol}${price}/mo`, sub: 'Trial ended · Renew anytime to resume access', color: '#6366f1' },
    active:   { label: '✓ Active',                            sub: `Subscribed · ${symbol}${price}/mo`,          color: '#16a34a' },
  };
}

interface ModuleCardProps {
  mod: ModuleDef;
  currency: string;
  status: ModuleStatus;
  loading: boolean;
  anyModuleInTrial: boolean;
  freeSlotUsed: boolean;
  onStartTrial: (id: ModuleId) => void;
  onSubscribe: (id: ModuleId) => void;
  trialEndDate?: string;
  outletCount: number;
  onOutletCountChange: (id: ModuleId, count: number) => void;
  activeOutletCount: number;
  pendingOutletCount?: number;
  onRequestOutletChange: (id: ModuleId, count: number) => Promise<boolean>;
  seatCount: number;
  pendingSeatCount?: number;
  seatsIncluded: number;
  seatAddonPrice: number;
  onRequestSeatChange: (id: ModuleId, count: number) => Promise<boolean>;
  onRequestCombinedChange: (id: ModuleId, outletCount: number, seatCount: number) => Promise<boolean>;
  queueOutlets?: { id: number; name: string }[];
  queueCounters?: { id: number; code: string; name: string; outlet_id: number | null; locked: boolean }[];
  selectedQueueOutletId?: number | null;
  onSelectQueueOutlet?: (id: number) => void;
  selectedQueueCounterIds?: number[];
  onToggleQueueCounter?: (id: number) => void;
  onPurchaseQueueCounters?: () => void;
  purchasingQueueCounters?: boolean;
  onCreateQueueOutlet?: (name: string) => Promise<void>;
  creatingQueueOutlet?: boolean;
  onCreateQueueCounter?: (outletId: number, name: string) => Promise<void>;
  creatingQueueCounter?: boolean;
  bundlePriceFor5?: number | null;
  moduleOutlets?: { id: number; name: string; locked?: boolean; billedSince?: string | null; expires?: string | null }[];
  pricePerLockedOutlet?: number;
  onReactivateOutlets?: (moduleId: ModuleId, outletIds: number[]) => void;
  reactivating?: boolean;
  onManageOutlets?: (id: ModuleId) => void;
}
function QueueCounterPanel({
  currency,
  outlets,
  selectedOutletId,
  onSelectOutlet,
  counters,
  selectedCounterIds,
  onToggleCounter,
  onPurchase,
  purchasing,
  onCreateOutlet,
  creatingOutlet,
  onCreateCounter,
  creatingCounter,
  pricePerCounter,
  bundlePriceFor5,
  maxCounters = 5,
}: {
  currency: string;
  outlets: { id: number; name: string }[];
  selectedOutletId: number | null;
  onSelectOutlet: (id: number) => void;
  counters: { id: number; code: string; name: string; outlet_id: number | null; locked: boolean }[];
  selectedCounterIds: number[];
  onToggleCounter: (id: number) => void;
  onPurchase: () => void;
  purchasing: boolean;
  onCreateOutlet: (name: string) => Promise<void>;
  creatingOutlet: boolean;
  onCreateCounter: (outletId: number, name: string) => Promise<void>;
  creatingCounter: boolean;
  pricePerCounter: number;
  bundlePriceFor5?: number | null;
  maxCounters?: number;
}) {
  const [showNewOutlet, setShowNewOutlet] = useState(false);
  const [newOutletName, setNewOutletName] = useState('');
  const [showNewCounter, setShowNewCounter] = useState(false);
  const [newCounterName, setNewCounterName] = useState('');

  const handleCreateOutlet = async () => {
    if (!newOutletName.trim()) return;
    await onCreateOutlet(newOutletName.trim());
    setNewOutletName('');
    setShowNewOutlet(false);
  };

  const handleCreateCounter = async () => {
    if (!newCounterName.trim() || selectedOutletId == null) return;
    await onCreateCounter(selectedOutletId, newCounterName.trim());
    setNewCounterName('');
    setShowNewCounter(false);
  };

  const newOutletBlock = showNewOutlet ? (
    <div style={{ marginBottom: 10, padding: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
      <input
        value={newOutletName}
        onChange={e => setNewOutletName(e.target.value)}
        placeholder="Outlet name, e.g. Second Branch"
        style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12, color: '#0f172a', marginBottom: 8, boxSizing: 'border-box' }}
      />
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={handleCreateOutlet}
          disabled={creatingOutlet || !newOutletName.trim()}
          style={{ flex: 1, padding: '7px', borderRadius: 7, border: 'none', background: creatingOutlet ? '#e2e8f0' : '#6366f1', color: creatingOutlet ? '#94a3b8' : '#fff', fontWeight: 700, fontSize: 11, cursor: creatingOutlet ? 'default' : 'pointer' }}
        >
          {creatingOutlet ? 'Creating...' : 'Create outlet'}
        </button>
        <button
          onClick={() => { setShowNewOutlet(false); setNewOutletName(''); }}
          style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  ) : (
    <button
      onClick={() => setShowNewOutlet(true)}
      style={{ width: '100%', marginBottom: 10, padding: '7px', borderRadius: 8, border: '1.5px dashed #c7d2fe', background: '#eef2ff', color: '#6366f1', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}
    >
      + New outlet for Queue
    </button>
  );

  if (outlets.length === 0) {
    return (
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>Counters per outlet</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>
          No outlets set up for Queue yet — create one below.
        </div>
        {newOutletBlock}
      </div>
    );
  }

  const outletCounters = counters.filter(c => c.outlet_id === selectedOutletId);
  const lockedCounters = outletCounters.filter(c => c.locked);
  const unlockedCount = outletCounters.length - lockedCounters.length;
  const selectedInThisOutlet = selectedCounterIds.filter(id => outletCounters.some(c => c.id === id));
  const selectedCount = selectedInThisOutlet.length;
  const canAddMore = outletCounters.length < maxCounters;

  const isBundle = selectedCount === maxCounters && bundlePriceFor5 != null;
  const total = isBundle ? bundlePriceFor5! : pricePerCounter * selectedCount;
  const regularTotal = pricePerCounter * selectedCount;

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 8 }}>Counters per outlet</div>

      {newOutletBlock}

      <select
        value={selectedOutletId ?? ''}
        onChange={e => onSelectOutlet(Number(e.target.value))}
        style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, color: '#0f172a', marginBottom: 8 }}
      >
        {outlets.map(o => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>

      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 8 }}>
        {unlockedCount} of {outletCounters.length} counter{outletCounters.length !== 1 ? 's' : ''} active
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
        {outletCounters.map(c => {
          const isSelected = selectedCounterIds.includes(c.id);
          if (!c.locked) {
            return (
              <div key={c.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '7px 10px', borderRadius: 8, border: '1px solid #dcfce7', background: '#f0fdf4',
              }}>
                <span style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>{c.name}</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: '#16a34a' }}>✓ Active</span>
              </div>
            );
          }
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onToggleCounter(c.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                borderRadius: 8, border: `1px solid ${isSelected ? '#6366f1' : '#e2e8f0'}`,
                background: isSelected ? '#eef2ff' : '#fff', cursor: 'pointer', width: '100%', textAlign: 'left',
              }}
            >
              <div style={{
                width: 15, height: 15, borderRadius: 4, border: '1.5px solid #cbd5e1',
                background: isSelected ? '#6366f1' : '#fff',
                borderColor: isSelected ? '#6366f1' : '#cbd5e1',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {isSelected && <span style={{ color: '#fff', fontSize: 9, fontWeight: 800 }}>✓</span>}
              </div>
              <span style={{ fontSize: 11, color: isSelected ? '#4338ca' : '#334155', fontWeight: isSelected ? 700 : 600 }}>
                {c.name} — locked · {currency}{pricePerCounter}/mo
              </span>
            </button>
          );
        })}
      </div>

      {showNewCounter ? (() => {
        const trimmedNew = newCounterName.trim();
        const liveDup = trimmedNew
          ? outletCounters.find(c => c.name.trim().toLowerCase() === trimmedNew.toLowerCase())
          : null;
        return (
        <div style={{ marginBottom: 10, padding: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          <input
            value={newCounterName}
            onChange={e => setNewCounterName(e.target.value)}
            placeholder="Counter name, e.g. Counter 3"
            style={{
              width: '100%', padding: '7px 10px', borderRadius: 7,
              border: `1.5px solid ${liveDup ? '#fecaca' : '#e2e8f0'}`,
              fontSize: 12, color: '#0f172a', marginBottom: liveDup ? 4 : 8, boxSizing: 'border-box',
            }}
          />
          {liveDup && (
            <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, marginBottom: 8 }}>
              ⚠ "{liveDup.name}" already exists on this outlet
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={handleCreateCounter}
              disabled={creatingCounter || !trimmedNew || !!liveDup}
              style={{
                flex: 1, padding: '7px', borderRadius: 7, border: 'none',
                background: (creatingCounter || !!liveDup) ? '#e2e8f0' : '#6366f1',
                color: (creatingCounter || !!liveDup) ? '#94a3b8' : '#fff',
                fontWeight: 700, fontSize: 11, cursor: (creatingCounter || !!liveDup) ? 'default' : 'pointer',
              }}
            >
              {creatingCounter ? 'Creating...' : 'Add counter'}
            </button>
            <button
              onClick={() => { setShowNewCounter(false); setNewCounterName(''); }}
              style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
        );
      })() : (
        <button
          onClick={() => setShowNewCounter(true)}
          disabled={!canAddMore}
          style={{
            width: '100%', marginBottom: 10, padding: '7px', borderRadius: 8,
            border: `1.5px dashed ${canAddMore ? '#c7d2fe' : '#e2e8f0'}`,
            background: canAddMore ? '#eef2ff' : '#f8fafc',
            color: canAddMore ? '#6366f1' : '#cbd5e1',
            fontWeight: 700, fontSize: 11, cursor: canAddMore ? 'pointer' : 'default',
          }}
        >
          {canAddMore ? '+ Add counter' : `Max ${maxCounters} counters per outlet`}
        </button>
      )}

      {selectedCount > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>
            {currency}{total}/mo
            {isBundle ? (
              <span style={{ fontWeight: 700, color: '#16a34a' }}> · bundle price (was {currency}{regularTotal})</span>
            ) : (
              <span style={{ fontWeight: 500, color: '#94a3b8' }}> ({currency}{pricePerCounter}/counter × {selectedCount})</span>
            )}
          </div>

          <button
            onClick={onPurchase}
            disabled={purchasing}
            style={{
              marginTop: 10, width: '100%', padding: '9px', borderRadius: 8, border: 'none',
              background: purchasing ? '#e2e8f0' : '#6366f1', color: purchasing ? '#94a3b8' : '#fff',
              fontWeight: 700, fontSize: 12, cursor: purchasing ? 'default' : 'pointer',
            }}
          >
            {purchasing ? '...' : `Subscribe for ${selectedCount} counter${selectedCount !== 1 ? 's' : ''} — ${currency}${total}/mo`}
          </button>
        </>
      )}
    </div>
  );
}

interface LockedOutlet { outlet_id: number; outlet_name: string }
interface OutletRow {
  id: number;
  name: string;
  locked?: boolean;
  needs_outlet_selection?: boolean;
  modules?: string[];
  module_caps?: Record<string, number>;
  module_billed_since?: Record<string, string | null>;
  module_expires?: Record<string, string | null>;
  module_locked?: Record<string, boolean>;
}

function formatShortDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function isDedicatedToModule(o: OutletRow, modId: string): boolean {
  if ((o.modules || []).includes(modId)) return true;
  if (o.module_locked && modId in o.module_locked) return true;
  if (o.module_billed_since && modId in o.module_billed_since) return true;
  if (o.module_expires && modId in o.module_expires) return true;
  if (o.name.toLowerCase().trim() === modId.toLowerCase().trim()) return true;
  return false;
}

function OutletsStatusModal({
  moduleId, moduleName, outlets, pricePerOutlet, currency, onReactivate, saving, onClose,
}: {
  moduleId: string;
  moduleName: string;
  outlets: { id: number; name: string; locked?: boolean; billedSince?: string | null; expires?: string | null }[];
  pricePerOutlet: number;
  currency: string;
  onReactivate: (outletIds: number[]) => void;
  saving: boolean;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<number[]>([]);

  const toggle = (id: number) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const total = pricePerOutlet * selected.length;

  const modalContent = (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(15,23,42,0.5)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div 
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 20, width: '100%', maxWidth: 390,
          padding: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          border: '1px solid #f1f5f9',
          fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{moduleName} — Outlets</div>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer', padding: 4, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
        <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 14px' }}>
          Tick inactive outlets to reactivate them.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16, maxHeight: 320, overflowY: 'auto' }}>
          {outlets.map(o => {
            const isLocked = !!o.locked;
            const isSelected = selected.includes(o.id);
            return (
              <button
                key={o.id}
                type="button"
                disabled={!isLocked}
                onClick={() => isLocked && toggle(o.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  padding: '10px 12px', borderRadius: 10,
                  border: `1.5px solid ${isSelected ? '#6366f1' : '#e2e8f0'}`,
                  background: isSelected ? '#f5f3ff' : '#fff',
                  cursor: isLocked ? 'pointer' : 'default', width: '100%', textAlign: 'left',
                  opacity: isLocked ? 1 : 0.85,
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {isLocked && (
                    <div style={{
                      width: 18, height: 18, borderRadius: 5, border: '1.5px solid #cbd5e1',
                      background: isSelected ? '#6366f1' : '#fff',
                      borderColor: isSelected ? '#6366f1' : '#cbd5e1',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {isSelected && <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>✓</span>}
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 12, color: isSelected ? '#4338ca' : '#1e293b', fontWeight: isSelected ? 800 : 700 }}>
                      {o.name}
                    </div>
                    {(() => {
                      const isCreditBookFreeOutlet = moduleId === 'ledger' && !isLocked && !o.expires;
                      return (
                        <div style={{ fontSize: 9.5, color: '#94a3b8', fontWeight: 500, marginTop: 2 }}>
                          {isCreditBookFreeOutlet
                            ? 'Free forever · No renewal required'
                            : isLocked
                            ? `${formatShortDate(o.expires) ? `Expired on ${formatShortDate(o.expires)} · ` : ''}Select to reactivate`
                            : `${formatShortDate(o.billedSince) ? `Cycle: ${formatShortDate(o.billedSince)} – ` : ''}${formatShortDate(o.expires) ? `Renews ${formatShortDate(o.expires)}` : 'Active'}`}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {(() => {
                  const isCreditBookFreeOutlet = moduleId === 'ledger' && !isLocked && !o.expires;
                  return (
                    <span style={{
                      fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 99,
                      background: isLocked ? '#fef2f2' : isCreditBookFreeOutlet ? '#eef2ff' : '#f0fdf4',
                      color: isLocked ? '#dc2626' : isCreditBookFreeOutlet ? '#4338ca' : '#16a34a',
                      alignSelf: 'flex-start',
                    }}>
                      {isLocked ? 'Inactive' : isCreditBookFreeOutlet ? 'Free Forever' : 'Active'}
                    </span>
                  );
                })()}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => onReactivate(selected)}
          disabled={saving || selected.length === 0}
          style={{
            width: '100%', padding: '11px', borderRadius: 12, border: 'none',
            background: saving || selected.length === 0 ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1, #4f46e5)',
            color: saving || selected.length === 0 ? '#94a3b8' : '#fff',
            fontWeight: 800, fontSize: 13, cursor: saving || selected.length === 0 ? 'default' : 'pointer',
            boxShadow: selected.length > 0 ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none',
            transition: 'all 0.15s ease',
          }}
        >
          {saving ? '...' : selected.length === 0 ? 'Select outlets to reactivate' : `Reactivate selected — ${currency}${total}/mo`}
        </button>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
}



function LockedOutletsPanel({
  lockedOutlets, pricePerOutlet, currency, onReactivate, saving,
}: {
  lockedOutlets: LockedOutlet[];
  pricePerOutlet: number;
  currency: string;
  onReactivate: (outletIds: number[]) => void;
  saving: boolean;
}) {
  const [selected, setSelected] = useState<number[]>([]);

  const toggle = (id: number) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  if (lockedOutlets.length === 0) return null;

  const total = pricePerOutlet * selected.length;

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', marginBottom: 8 }}>
        Locked outlets — select to reactivate
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
        {lockedOutlets.map(o => {
          const isSelected = selected.includes(o.outlet_id);
          return (
            <button
              key={o.outlet_id}
              type="button"
              onClick={() => toggle(o.outlet_id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                borderRadius: 8, border: `1px solid ${isSelected ? '#6366f1' : '#e2e8f0'}`,
                background: isSelected ? '#eef2ff' : '#fff', cursor: 'pointer',
                width: '100%', textAlign: 'left',
              }}
            >
              <div style={{
                width: 16, height: 16, borderRadius: 4, border: '1.5px solid #cbd5e1',
                background: isSelected ? '#6366f1' : '#fff',
                borderColor: isSelected ? '#6366f1' : '#cbd5e1',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {isSelected && <span style={{ color: '#fff', fontSize: 10, fontWeight: 800 }}>✓</span>}
              </div>
              <span style={{ fontSize: 12, color: isSelected ? '#4338ca' : '#334155', fontWeight: isSelected ? 700 : 600 }}>
                {o.outlet_name} — {currency}{pricePerOutlet}/mo
              </span>
            </button>
          );
        })}
      </div>
      <button
        onClick={() => onReactivate(selected)}
        disabled={saving || selected.length === 0}
        style={{
          width: '100%', padding: '9px', borderRadius: 8, border: 'none',
          background: saving || selected.length === 0 ? '#e2e8f0' : '#6366f1',
          color: saving || selected.length === 0 ? '#94a3b8' : '#fff',
          fontWeight: 700, fontSize: 12, cursor: saving || selected.length === 0 ? 'default' : 'pointer',
        }}
      >
        {saving ? '...' : selected.length === 0 ? 'Select outlets to reactivate' : `Reactivate selected — ${currency}${total}/mo`}
      </button>
    </div>
  );
}

function ModuleCard({ mod, currency, status, loading, anyModuleInTrial, freeSlotUsed, trialEndDate, onStartTrial, onSubscribe, outletCount, onOutletCountChange, activeOutletCount, pendingOutletCount, onRequestOutletChange, seatCount, pendingSeatCount, seatsIncluded, seatAddonPrice, onRequestSeatChange, onRequestCombinedChange, queueOutlets, queueCounters, selectedQueueOutletId, onSelectQueueOutlet, selectedQueueCounterIds, onToggleQueueCounter, onPurchaseQueueCounters, purchasingQueueCounters, onCreateQueueOutlet, creatingQueueOutlet, onCreateQueueCounter, creatingQueueCounter, bundlePriceFor5, moduleOutlets, pricePerLockedOutlet, onReactivateOutlets, reactivating, onManageOutlets }: ModuleCardProps) {
  const [showOutletsModal, setShowOutletsModal] = useState(false);
  const isActive = status === 'active';
  const [requestedCount, setRequestedCount] = useState(pendingOutletCount ?? activeOutletCount);
  const [requestedSeatCount, setRequestedSeatCount] = useState(pendingSeatCount ?? seatCount);

  useEffect(() => {
    setRequestedCount(pendingOutletCount ?? activeOutletCount);
  }, [pendingOutletCount, activeOutletCount]);
  useEffect(() => {
    setRequestedSeatCount(pendingSeatCount ?? seatCount);
  }, [pendingSeatCount, seatCount]);
  const symbol = mod.monthlyCurrency;

  // Once active, pricing must reflect the REAL outlet count (activeOutletCount),
  // not the pre-purchase stepper's outletCount — that stepper is hidden once
  // active and its value (default 1) would otherwise stay stale forever,
  // showing wrong pricing/labels even after outlets are added or removed.
  // While active, preview the top price using the pending stepper value
  // (requestedCount) rather than the currently-billed activeOutletCount —
  // so moving the outlet/seat steppers updates the headline price live,
  // instead of only the "New total once confirmed" line further down.
  const displayOutletCount = isActive ? requestedCount : outletCount;
  const billedPrice = mod.monthlyPrice * billableOutlets(mod.id, displayOutletCount, freeSlotUsed);
  const isFreeForever = FREE_FIRST_OUTLET_MODULES.has(mod.id) && displayOutletCount <= 1 && !freeSlotUsed;

  // Credit Book 1st outlet is free forever — NEVER flash it as locked!
  const effectiveStatus: ModuleStatus = isFreeForever
    ? 'active'
    : (status === 'inactive' && anyModuleInTrial) ? 'locked' : status;
  const meta = getStatusMeta(symbol, billedPrice)[effectiveStatus];
  const daysLeft = trialEndDate
    ? Math.max(0, Math.ceil((new Date(trialEndDate.endsWith('Z') || trialEndDate.includes('+') ? trialEndDate : trialEndDate + 'Z').getTime() - Date.now()) / 86400000))
    : null;

  const handleClick = () => {
    if (isActive || loading) return;
    if (status === 'inactive' && !anyModuleInTrial) { onStartTrial(mod.id); return; }
    onSubscribe(mod.id);
  };

  const cardColor = isActive ? '#16a34a' : status === 'locked' ? '#dc2626' : '#6366f1';

  return (
    <div style={{
      borderRadius: 20, width: '100%', maxWidth: 440,
      border: `2px solid ${isActive ? '#16a34a' : status === 'locked' ? '#fecaca' : '#6366f133'}`,
      background: isActive ? '#f0fdf4' : '#fff',
      padding: '28px 24px 20px',
      display: 'flex', flexDirection: 'column', gap: 16, position: 'relative',
      boxShadow: '0 4px 20px rgba(99,102,241,0.08)',
      transition: 'all 0.2s',
    }}>

      {/* Icon + name */}
      <div>
        <div style={{ fontSize: 28, marginBottom: 6 }}>{mod.icon}</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>{mod.name}</div>
        <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0', lineHeight: 1.4 }}>{mod.desc}</p>
      </div>

      {/* Price */}
      <div>
        {mod.id === 'queue' ? (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
            <span style={{ fontSize: 28, fontWeight: 900, color: '#16a34a', lineHeight: 1 }}>Free</span>
            <span style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>— pay only for counters you use</span>
          </div>
        ) : billedPrice === 0 ? (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
            <span style={{ fontSize: 28, fontWeight: 900, color: '#16a34a', lineHeight: 1 }}>Free</span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>{symbol}</span>
            <span style={{ fontSize: 36, fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{billedPrice}</span>
            <span style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>/mo</span>
          </div>
        )}
        <div style={{ fontSize: 11, color: cardColor, fontWeight: 700, marginTop: 4 }}>
          {status === 'trial' && daysLeft !== null ? `⏳ ${daysLeft} days left in trial` : meta.sub}
        </div>

        {mod.id === 'queue' ? (
          status !== 'inactive' && (
            <QueueCounterPanel
              currency={currency}
              outlets={queueOutlets || []}
              selectedOutletId={selectedQueueOutletId ?? null}
              onSelectOutlet={onSelectQueueOutlet || (() => {})}
              counters={queueCounters || []}
              selectedCounterIds={selectedQueueCounterIds || []}
              onToggleCounter={onToggleQueueCounter || (() => {})}
              onPurchase={onPurchaseQueueCounters || (() => {})}
              purchasing={!!purchasingQueueCounters}
              onCreateOutlet={onCreateQueueOutlet || (async () => {})}
              creatingOutlet={!!creatingQueueOutlet}
              onCreateCounter={onCreateQueueCounter || (async () => {})}
              creatingCounter={!!creatingQueueCounter}
              pricePerCounter={mod.monthlyPrice}
              bundlePriceFor5={bundlePriceFor5}
            />
          )
        ) : (
        <>
        {/* Outlet stepper — only editable before an active paid subscription */}
        {!isActive && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
            <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Outlets:</span>
            <button
              onClick={() => onOutletCountChange(mod.id, Math.max(1, outletCount - 1))}
              disabled={outletCount <= 1}
              style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: outletCount <= 1 ? 'default' : 'pointer', fontWeight: 700, color: '#475569' }}
            >−</button>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', minWidth: 16, textAlign: 'center' }}>{outletCount}</span>
            <button
              onClick={() => onOutletCountChange(mod.id, outletCount + 1)}
              style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 700, color: '#475569' }}
            >+</button>
            <span style={{ fontSize: 10, color: '#94a3b8' }}>
              {FREE_FIRST_OUTLET_MODULES.has(mod.id) && !freeSlotUsed
                ? `1 free, +${symbol}${mod.monthlyPrice}/extra`
                : `${symbol}${mod.monthlyPrice}/outlet`}
            </span>
          </div>
        )}
        {isActive && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Outlets:</span>
              <button
                onClick={() => setRequestedCount(c => Math.max(1, c - 1))}
                disabled={requestedCount <= 1}
                style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: requestedCount <= 1 ? 'default' : 'pointer', fontWeight: 700, color: '#475569' }}
              >−</button>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', minWidth: 16, textAlign: 'center' }}>{requestedCount}</span>
              <button
                onClick={() => setRequestedCount(c => c + 1)}
                style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 700, color: '#475569' }}
              >+</button>
              <span style={{ fontSize: 10, color: '#94a3b8' }}>{activeOutletCount} active now</span>
            </div>

            {/* Dedicated Outlet Display */}
            {moduleOutlets && moduleOutlets.length > 0 && (
              <div style={{ marginTop: 8, padding: '6px 10px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b', fontWeight: 600 }}>Dedicated Outlet:</span>
                <span style={{ fontWeight: 800, color: moduleOutlets.some(o => !o.locked) ? '#16a34a' : '#dc2626' }}>
                  {moduleOutlets.filter(o => !o.locked).map(o => `🟢 ${o.name}`).join(', ') || `🔒 ${moduleOutlets.map(o => o.name).join(', ')} (Inactive)`}
                </span>
              </div>
            )}

            {requestedCount !== activeOutletCount && requestedCount < activeOutletCount && (
              <button
                onClick={() => onRequestOutletChange(mod.id, requestedCount)}
                style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: '#fff', background: '#6366f1', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}
              >
                Remove outlets (no charge)
              </button>
            )}

            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Seats:</span>
                <button
                  onClick={() => setRequestedSeatCount(c => Math.max(0, c - 1))}
                  disabled={requestedSeatCount <= 0}
                  style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: requestedSeatCount <= 0 ? 'default' : 'pointer', fontWeight: 700, color: '#475569' }}
                >−</button>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', minWidth: 16, textAlign: 'center' }}>{requestedSeatCount}</span>
                <button
                  onClick={() => setRequestedSeatCount(c => c + 1)}
                  style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 700, color: '#475569' }}
                >+</button>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>extra · {seatsIncluded} included, +{currency}{seatAddonPrice}/seat</span>
              </div>

              {requestedSeatCount !== seatCount && requestedSeatCount < seatCount && (
                <button
                  onClick={() => onRequestSeatChange(mod.id, requestedSeatCount)}
                  style={{ marginTop: 8, fontSize: 11, fontWeight: 700, color: '#fff', background: '#6366f1', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}
                >
                  Remove seats (no charge)
                </button>
              )}

              {(requestedCount > activeOutletCount || requestedSeatCount > seatCount) && (
                <button
                  onClick={() => onRequestCombinedChange(mod.id, requestedCount, requestedSeatCount)}
                  style={{ marginTop: 8, width: '100%', fontSize: 12, fontWeight: 700, color: '#fff', background: '#6366f1', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}
                >
                  💳 Pay & confirm changes
                </button>
              )}

              {(requestedCount !== activeOutletCount || requestedSeatCount !== seatCount) && (
                <div style={{ marginTop: 8, fontSize: 11, fontWeight: 800, color: '#0f172a' }}>
                  New total once confirmed: {currency}
                  {mod.monthlyPrice * billableOutlets(mod.id, requestedCount, freeSlotUsed) + seatAddonPrice * requestedSeatCount}/mo
                  <span style={{ fontWeight: 500, color: '#94a3b8' }}> (current: {currency}{mod.monthlyPrice * billableOutlets(mod.id, activeOutletCount, freeSlotUsed) + seatAddonPrice * seatCount}/mo)</span>
                  {(requestedSeatCount > seatCount || requestedCount > activeOutletCount) && (
                    <div style={{ marginTop: 4, fontWeight: 600, color: '#6366f1' }}>
                      You'll be charged a prorated amount now for the rest of this cycle.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        {moduleOutlets && moduleOutlets.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9', display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={() => setShowOutletsModal(true)}
              style={{
                flex: 1, padding: '9px', borderRadius: 8, border: '1.5px solid #e2e8f0',
                background: '#fff', color: '#334155', fontWeight: 700, fontSize: 11, cursor: 'pointer',
              }}
            >
              🏬 Outlets ({moduleOutlets.filter(o => o.locked).length} inactive)
            </button>
            <button
              type="button"
              onClick={() => onManageOutlets?.(mod.id)}
              style={{
                padding: '9px 12px', borderRadius: 8, border: '1.5px solid #c7d2fe',
                background: '#eef2ff', color: '#6366f1', fontWeight: 700, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
              }}
              title="Select which outlets are active"
            >
              ⚙ Select Active
            </button>
          </div>
        )}
        {showOutletsModal && moduleOutlets && (
          <OutletsStatusModal
            moduleId={mod.id}
            moduleName={mod.name}
            outlets={moduleOutlets}
            pricePerOutlet={pricePerLockedOutlet ?? mod.monthlyPrice}
            currency={currency}
            saving={!!reactivating}
            onClose={() => setShowOutletsModal(false)}
            onReactivate={(outletIds) => { onReactivateOutlets?.(mod.id, outletIds); setShowOutletsModal(false); }}
          />
        )}
        </>
        )}
      </div>

      {/* Features */}
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        {mod.features.map(f => (
          <li key={f} style={{ fontSize: 12, color: '#334155', fontWeight: 500 }}>{f}</li>
        ))}
      </ul>

      {effectiveStatus === 'locked' && billedPrice > 0 && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 11, color: '#475569', fontWeight: 500, lineHeight: 1.4 }}>
          🔒 Access is temporarily paused. Your records are safely saved — subscribe anytime to continue.
        </div>
      )}

      {/* Button — Queue is per-outlet billed via the counter panel above,
          not this flat button. Only show it for the trial-start case
          (still needed to create the base Queue Subscription row); once
          trial/active/locked, real payment happens per-outlet instead. */}
      {mod.id === 'queue' && status !== 'inactive' ? (
        <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', fontWeight: 600 }}>
          {status === 'trial'
            ? 'Free during trial — every counter is unlocked until it ends'
            : 'Module access is free forever — subscribe individual counters above to unlock them'}
        </div>
      ) : (
        <button
          onClick={handleClick}
          disabled={isActive || loading}
          style={{
            width: '100%', padding: '11px', borderRadius: 12, border: 'none',
            background: isActive ? '#dcfce7' : cardColor,
            color: isActive ? '#16a34a' : '#fff',
            fontWeight: 800, fontSize: 13,
            cursor: isActive || loading ? 'default' : 'pointer',
            opacity: loading ? 0.6 : 1, transition: 'all 0.15s',
          }}
        >
          {loading
            ? '...'
            : isActive
            ? meta.label
            : status === 'inactive' && !anyModuleInTrial
            ? meta.label
            : mod.monthlyPrice === 0 || billableOutlets(mod.id, outletCount, freeSlotUsed) === 0
            ? `${meta.label.split(' —')[0]} — Free`
            : `${meta.label.split(' —')[0]} — ${symbol}${mod.monthlyPrice * billableOutlets(mod.id, outletCount, freeSlotUsed)}/mo`}
        </button>
      )}
    </div>
  );

}

// ── Individual Suite Plan Card ──────────────────────────────────────────────
interface PlanCardProps {
  planId: PlanId;
  cycle: BillingCycle;
  currentPlan?: PlanId;
  onSelect: (plan: PlanId) => void;
  onSelectPaid: (plan: PlanId) => void;
  prices: typeof DEFAULT_PRICES;
  currency: string;
  hadTrial: boolean;
  planStatus: string;
  currentUserData?: any;
  hasActiveModules?: boolean;
  seatCount?: number;
  pendingSeatCount?: number;
  seatsIncluded?: number;
  seatAddonPrice?: number;
  onRequestSeatChange?: (plan: PlanId, count: number) => void;
}

const FULL_PLAN_META: Record<PlanId, { name: string; color: string; accent: string; badge?: string; tagline: string }> = {
  basic: { name: 'Basic', color: '#64748b', accent: '#f1f5f9', tagline: 'Get started, no credit card' },
  pro:   { name: 'Pro',   color: '#6366f1', accent: '#eef2ff', badge: 'Most Popular', tagline: '7-day free trial · Everything a growing business needs' },
  ultra: { name: 'Ultra', color: '#7c3aed', accent: '#f5f3ff', badge: 'Full Power',    tagline: '7-day free trial · Unlimited invoices & dedicated account manager' },
};

function PlanCard({ planId, cycle, currentPlan, onSelect, onSelectPaid, prices, currency, hadTrial, currentUserData, hasActiveModules, seatCount = 0, pendingSeatCount, seatsIncluded = 1, seatAddonPrice = 30, onRequestSeatChange }: PlanCardProps) {
  const meta     = FULL_PLAN_META[planId];
  const price    = prices[planId][cycle];
  const cellCurrency = cycle === 'monthly' ? prices[planId].monthlyCurrency : prices[planId].yearlyCurrency;
  const seats    = SEAT_LIMITS[planId];
  const userCycle = currentUserData?.plan_billing_cycle as BillingCycle | undefined;
  const isFree    = price === 0;
  const isActive  = currentPlan === planId && (isFree || !userCycle || userCycle === cycle);
  const [requestedSeatCount, setRequestedSeatCount] = useState(pendingSeatCount ?? seatCount);

  useEffect(() => {
    setRequestedSeatCount(pendingSeatCount ?? seatCount);
  }, [pendingSeatCount, seatCount]);

  const basicFeatures: string[] = [
    `✅ ${BASIC_INVOICE_LIMIT} + 5 batch generate/mo`,
    '✅ 200+ invoice & receipt templates (A4, 80mm, 58mm thermal)',
    '✅ Customer directory & master product catalog',
    '✅ PDF export & document archiving',
    '✅ Scan-to-invoice dynamic QR code',
    '✅ Multi-currency + tax & discount calculation rules',
    '✅ WhatsApp & Email invoice delivery',
    '✅ 30+ payment gateways & payment links',
    '✅ 1 user license',
    '❌ Merge PDF + Excel export',
    '❌ Operational audit logs',
    '❌ Standalone Modules: POS, Payroll, Queue, Gym, Dining',
    '❌ Team seats',
  ];

  const planFeatures: Record<PlanId, string[]> = {
    basic: basicFeatures,
    pro: [
      '✅ 30 invoices & 30 batch operations / month',
      '✅ Everything in Basic included',
      '✅ Merge PDF engine + Excel spreadsheet export',
      '✅ Operational audit logs & activity history',
      '✅ WhatsApp & Email customer invoice delivery',
      '✅ 200+ bilingual templates (A4 & thermal)',
      '✅ 1 user license',
      '❌ Unlimited invoices (capped at 30/mo — Ultra needed)',
      '❌ Dedicated account manager',
      '❌ Standalone Modules: POS, Payroll, Queue, Gym, Dining',
      '❌ Team seats (Single-user individual license)',
    ],
    ultra: [
      '✅ Unlimited invoices & unlimited batch generation',
      '✅ Everything in Pro included',
      '✅ Operational audit logs & activity history',
      '✅ Dedicated account manager',
      '✅ Priority WhatsApp & Email support',
      '✅ Merge PDF + Excel export + 200+ templates',
      '✅ 1 user license (Full suite power)',
      '❌ Standalone Modules: POS, Payroll, Queue, Gym, Dining (Add via Module Plans)',
      '❌ Team multi-seats (Single-user individual license)',
    ],
  };

  const handleClick = () => {
    if (isActive) return;
    if (isFree || !hadTrial) { onSelect(planId); return; }
    onSelectPaid(planId);
  };

  const symbol = cellCurrency;
  const isSwitchingCycle = currentPlan === planId && !isFree && userCycle && userCycle !== cycle;

  const label = isActive ? '✓ Current Plan'
    : isFree ? 'Get Started Free'
    : isSwitchingCycle ? `🔁 Switch to ${cycle === 'monthly' ? 'Monthly' : 'Yearly'} — ${symbol}${price}/${cycle === 'monthly' ? 'mo' : 'yr'}`
    : !hadTrial ? '🎁 Start 7-day Free Trial'
    : `💳 Subscribe — ${symbol}${price}/${cycle === 'monthly' ? 'mo' : 'yr'}`;

  return (
    <div style={{
      flex: 1, minWidth: 220, maxWidth: 440, borderRadius: 20, width: '100%',
      border: `2px solid ${isActive ? meta.color : planId === 'pro' ? meta.color + '44' : '#e2e8f0'}`,
      background: isActive ? meta.accent : '#fff',
      padding: '28px 24px 24px',
      display: 'flex', flexDirection: 'column', gap: 20, position: 'relative',
      boxShadow: planId === 'pro' ? '0 8px 32px rgba(99,102,241,0.12)' : '0 2px 8px rgba(0,0,0,0.04)',
      transition: 'all 0.2s',
    }}>
      {meta.badge && (
        <div style={{
          position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
          background: meta.color, color: '#fff', fontSize: 10, fontWeight: 800,
          padding: '4px 14px', borderRadius: 99, whiteSpace: 'nowrap',
        }}>
          {meta.badge}
        </div>
      )}

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: meta.color }} />
          <span style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>{meta.name}</span>
        </div>
        <p style={{ fontSize: 11, color: '#94a3b8', margin: 0, lineHeight: 1.4 }}>
          {planId === 'basic' ? BASIC_TAGLINE : meta.tagline}
        </p>
      </div>

      <div>
        {isFree ? (
          <div style={{ fontSize: 36, fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>Free</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
              {cellCurrency}
            </span>
            <span style={{ fontSize: 36, fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{price}</span>
            <span style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>/{cycle === 'monthly' ? 'mo' : 'yr'}</span>
          </div>
        )}

        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{seats}</div>

      
      </div>

      {planId === 'basic' && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '6px 10px', fontSize: 11, color: '#c2410c', fontWeight: 600 }}>
          ⚠ Limited to {BASIC_INVOICE_LIMIT} — upgrade for unlimited
        </div>
      )}

      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
        {planFeatures[planId].map(f => (
          <li key={f} style={{ fontSize: 12, color: '#334155', fontWeight: 500 }}>{f}</li>
        ))}
      </ul>

      <button
        onClick={handleClick}
        style={{
          width: '100%', padding: '11px', borderRadius: 12, border: 'none',
          background: isActive ? meta.accent : planId === 'basic' ? '#f1f5f9' : meta.color,
          color: isActive ? meta.color : planId === 'basic' ? '#475569' : '#fff',
          fontWeight: 800, fontSize: 13,
          cursor: isActive ? 'default' : 'pointer',
          opacity: isActive ? 0.7 : 1, transition: 'all 0.15s',
        }}
      >
        {label}
      </button>
    </div>
  );
}

// ── 3D Coverflow Carousel wrapper for the three Suite plan cards ───────────
function PlanCarousel({ children, initialIndex = 1 }: { children: React.ReactNode[]; initialIndex?: number }) {
  const [index, setIndex] = useState(initialIndex);
  const total = children.length;
  const touchStartX = useRef(0);

  const goPrev = () => setIndex(i => (i - 1 + total) % total);
  const goNext = () => setIndex(i => (i + 1) % total);

  const handleTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx > 40) goPrev();
    else if (dx < -40) goNext();
  };

  const CARD_W = 440;
  const CARD_H = 760;

  return (
    <div>
      <div
        style={{ position: 'relative', height: CARD_H, perspective: 1400 }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {children.map((child, i) => {
          const offset = i - index;
          let transform = '';
          let zIndex = 1;
          let opacity = 0;
          if (offset === 0) {
            transform = 'translate(-50%, 0) translateZ(0) rotateY(0deg) scale(1)';
            zIndex = 3; opacity = 1;
          } else if (offset === -1) {
            transform = 'translate(calc(-50% - min(320px, 50vw)), 0) translateZ(-20px) rotateY(3deg) scale(0.94)';
            zIndex = 2; opacity = 0.5;
          } else if (offset === 1) {
            transform = 'translate(calc(-50% + min(320px, 50vw)), 0) translateZ(-20px) rotateY(-3deg) scale(0.94)';
            zIndex = 2; opacity = 0.5;
          } else {
            transform = 'translate(-50%, 0) translateZ(-340px) scale(0.7)';
            zIndex = 1; opacity = 0;
          }
          return (
            <div key={i} style={{
              position: 'absolute', top: 0, left: '50%', width: CARD_W, height: CARD_H,
              transform, zIndex, opacity, transition: 'transform 0.4s ease, opacity 0.4s ease',
              pointerEvents: offset === 0 ? 'auto' : 'none',
              boxShadow: offset === 0 ? '0 20px 60px rgba(15,23,42,0.14)' : 'none',
              borderRadius: 20,
            }}>
              <div className="spv-carousel-scroll" style={{
                height: '100%', width: '100%', overflowY: 'auto', overflowX: 'hidden',
                borderRadius: 20, paddingTop: 16,
              }}>
                <div style={{ minHeight: '100%', display: 'flex' }}>
                  <div style={{ flex: 1, display: 'flex' }}>{child}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, marginTop: 12 }}>
        <button onClick={goPrev} style={{
          width: 38, height: 38, borderRadius: '50%', border: '1.5px solid #e2e8f0', background: '#fff',
          cursor: 'pointer', fontSize: 16, color: '#475569',
        }}>‹</button>
        <div style={{ display: 'flex', gap: 6 }}>
          {children.map((_, i) => (
            <div key={i} style={{
              width: i === index ? 18 : 6, height: 6, borderRadius: 99,
              background: i === index ? '#6366f1' : '#e2e8f0', transition: 'all 0.2s',
            }} />
          ))}
        </div>
        <button onClick={goNext} style={{
          width: 38, height: 38, borderRadius: '50%', border: '1.5px solid #e2e8f0', background: '#fff',
          cursor: 'pointer', fontSize: 16, color: '#475569',
        }}>›</button>
      </div>
    </div>
  );
}

// ── Comparison Table ────────────────────────────────────────────────────────
function ComparisonTable() {
  const [open, setOpen] = useState(false);

  const rows: PlanFeature[] = [
    { label: 'Invoices / month',         basic: '5/mo',  pro: '30/mo', ultra: 'Unlimited', highlight: true },
    { label: 'Batch Generate / month',   basic: '5/mo',  pro: '30/mo', ultra: 'Unlimited', highlight: true },
    { label: 'Email support',            basic: true,    pro: true,  ultra: true },
    { label: 'WhatsApp API support',     basic: true,    pro: true,  ultra: true },
    { label: '30+ payment gateways (India & international)', basic: true, pro: true, ultra: true },
    { label: 'Invoice Creator',          basic: true,    pro: true,  ultra: true },
    { label: 'Quick Invoice',            basic: true,    pro: true,  ultra: true },
    { label: 'Saved Invoices',           basic: true,    pro: true,  ultra: true },
    { label: 'Client Accounts',          basic: true,    pro: true,  ultra: true },
    { label: 'Dashboard',                basic: true,    pro: true,  ultra: true },
    { label: 'Profile',                  basic: true,    pro: true,  ultra: true },
    { label: 'PDF export',               basic: true,    pro: true,  ultra: true },
    { label: 'All templates',            basic: true,    pro: true,  ultra: true },
    { label: 'Product catalog',          basic: true,    pro: true,  ultra: true },
    { label: 'Multi-currency',           basic: true,    pro: true,  ultra: true },
    { label: 'Scan-to-invoice QR code',  basic: true,    pro: true,  ultra: true },
    { label: 'Tax / Shipping / Discount',basic: true,    pro: true,  ultra: true },
    { label: 'Bilingual invoices',       basic: true,    pro: true,  ultra: true },
    { label: 'Translation (100+ languages)', basic: true, pro: true, ultra: true },
    { label: 'WhatsApp send',            basic: true,    pro: true,  ultra: true },
    { label: 'Email reminders',          basic: true,    pro: true,  ultra: true },
    { label: 'Merge PDF / Excel',        basic: false,   pro: true,  ultra: true },
    { label: 'Audit logs',               basic: false,   pro: true,  ultra: true },


    { label: 'Dedicated account manager', basic: false,   pro: false, ultra: true },
    { label: 'Team seats',               basic: '1',     pro: '1',   ultra: '1' },
    { label: 'POS / Payroll / Modules',  basic: '❌ Add via Module Plans', pro: '❌ Add via Module Plans', ultra: '❌ Add via Module Plans', highlight: true },
  ];

  const plans: PlanId[] = ['basic', 'pro', 'ultra'];

  return (
    <div style={{ marginTop: 40 }}>
      <button onClick={() => setOpen(v => !v)} style={{ display: 'block', margin: '0 auto', background: 'none', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '8px 24px', fontSize: 12, fontWeight: 700, color: '#64748b', cursor: 'pointer' }}>
        {open ? '▲ Hide' : '▼ See full'} feature comparison
      </button>
      {open && (
        <div style={{ overflowX: 'auto', marginTop: 20 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                <th style={{ textAlign: 'left', padding: '10px 14px', color: '#94a3b8', fontWeight: 700, width: '40%' }}>Feature</th>
                {plans.map(p => (
                  <th key={p} style={{ padding: '10px 14px', textAlign: 'center', color: PLAN_META[p].color, fontWeight: 800, fontSize: 13 }}>
                    {PLAN_META[p].name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((f, i) => (
                <tr key={f.label} style={{ background: i % 2 === 0 ? '#fafafa' : '#fff', borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '9px 14px', color: '#334155', fontWeight: f.highlight ? 700 : 500 }}>{f.label}</td>
                  {plans.map(p => (
                    <td key={p} style={{ padding: '9px 14px', textAlign: 'center', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FeatureCell value={f[p]} planColor={PLAN_META[p].color} />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Module Plans Tab ────────────────────────────────────────────────────────
interface ModulePlansTabProps {
  currency: string;
  queueBundle5Price: number | null;
  modules: ModuleDef[];
  statuses: Record<ModuleId, ModuleStatus>;
  loadingModuleId: ModuleId | null;
  anyModuleInTrial: boolean;
  freeSlotUsed: Record<string, boolean>;
  trialEndDates: Record<string, string>;
  onStartTrial: (id: ModuleId) => void;
  onSubscribe: (id: ModuleId) => void;
  outletCounts: Record<ModuleId, number>;
  onOutletCountChange: (id: ModuleId, count: number) => void;
  activeOutletCounts: Record<ModuleId, number>;
  pendingOutletCounts: Record<ModuleId, number | undefined>;
  onRequestOutletChange: (id: ModuleId, count: number) => Promise<boolean>;
  seatCounts: Record<ModuleId, number>;
  pendingSeatCounts: Record<ModuleId, number | undefined>;
  seatsIncluded: Record<ModuleId, number>;
  seatAddonPrice: number;
  onRequestSeatChange: (id: ModuleId, count: number) => Promise<boolean>;
  onRequestCombinedChange: (id: ModuleId, outletCount: number, seatCount: number) => Promise<boolean>;
  queueOutlets: { id: number; name: string }[];
  queueCounters: { id: number; code: string; name: string; outlet_id: number | null; locked: boolean }[];
  selectedQueueOutletId: number | null;
  onSelectQueueOutlet: (id: number) => void;
  selectedQueueCounterIds: number[];
  onToggleQueueCounter: (id: number) => void;
  onPurchaseQueueCounters: () => void;
  purchasingQueueCounters: boolean;
  onCreateQueueOutlet: (name: string) => Promise<void>;
  creatingQueueOutlet: boolean;
  onCreateQueueCounter: (outletId: number, name: string) => Promise<void>;
  creatingQueueCounter: boolean;
  allOutlets: OutletRow[];
  lockedOutletPrices: Record<string, number>;
  onReactivateOutlets: (moduleId: ModuleId, outletIds: number[]) => void;
  reactivatingModuleId: ModuleId | null;
  onManageOutlets?: (id: ModuleId) => void;
}
function ModulePlansTab({ currency, queueBundle5Price, modules, statuses, loadingModuleId, anyModuleInTrial, freeSlotUsed, trialEndDates, onStartTrial, onSubscribe, outletCounts, onOutletCountChange, activeOutletCounts, pendingOutletCounts, onRequestOutletChange, seatCounts, pendingSeatCounts, seatsIncluded, seatAddonPrice, onRequestSeatChange, onRequestCombinedChange, queueOutlets, queueCounters, selectedQueueOutletId, onSelectQueueOutlet, selectedQueueCounterIds, onToggleQueueCounter, onPurchaseQueueCounters, purchasingQueueCounters, onCreateQueueOutlet, creatingQueueOutlet, onCreateQueueCounter, creatingQueueCounter, allOutlets, lockedOutletPrices, onReactivateOutlets, reactivatingModuleId, onManageOutlets }: ModulePlansTabProps) {
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ display: 'inline-block', background: '#f0fdf4', color: '#16a34a', fontSize: 11, fontWeight: 800, padding: '4px 14px', borderRadius: 99, letterSpacing: '0.06em', marginBottom: 12, textTransform: 'uppercase' }}>
          Standalone Module Plans
        </div>
        <h3 style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', margin: '0 0 8px' }}>
          Mix and match — pay only for what your business runs
        </h3>
        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
          Every module is fully independent · 7-day free trial, no card needed · Cancel or renew anytime.
        </p>
      </div>

      <PlanCarousel initialIndex={0}>
        {modules.map(mod => (
          <ModuleCard
            key={mod.id}
            mod={mod}
            currency={currency}
            status={statuses[mod.id] ?? 'inactive'}
            loading={loadingModuleId === mod.id}
            anyModuleInTrial={anyModuleInTrial}
            freeSlotUsed={freeSlotUsed[mod.id] ?? false}
            trialEndDate={trialEndDates[mod.id]}
            onStartTrial={onStartTrial}
            onSubscribe={onSubscribe}
            outletCount={outletCounts[mod.id] ?? 1}
            onOutletCountChange={onOutletCountChange}
            activeOutletCount={activeOutletCounts[mod.id] ?? 1}
            pendingOutletCount={pendingOutletCounts[mod.id]}
            onRequestOutletChange={onRequestOutletChange}
            seatCount={seatCounts[mod.id] ?? 0}
            pendingSeatCount={pendingSeatCounts[mod.id]}
            seatsIncluded={seatsIncluded[mod.id] ?? 10}
            seatAddonPrice={seatAddonPrice}
            onRequestSeatChange={onRequestSeatChange}
            onRequestCombinedChange={onRequestCombinedChange}
            queueOutlets={mod.id === 'queue' ? queueOutlets : undefined}
            queueCounters={mod.id === 'queue' ? queueCounters : undefined}
            selectedQueueOutletId={mod.id === 'queue' ? selectedQueueOutletId : undefined}
            onSelectQueueOutlet={mod.id === 'queue' ? onSelectQueueOutlet : undefined}
            selectedQueueCounterIds={mod.id === 'queue' ? selectedQueueCounterIds : undefined}
            onToggleQueueCounter={mod.id === 'queue' ? onToggleQueueCounter : undefined}
            onPurchaseQueueCounters={mod.id === 'queue' ? onPurchaseQueueCounters : undefined}
            purchasingQueueCounters={mod.id === 'queue' ? purchasingQueueCounters : undefined}
            onCreateQueueOutlet={mod.id === 'queue' ? onCreateQueueOutlet : undefined}
            creatingQueueOutlet={mod.id === 'queue' ? creatingQueueOutlet : undefined}
            onCreateQueueCounter={mod.id === 'queue' ? onCreateQueueCounter : undefined}
            creatingQueueCounter={mod.id === 'queue' ? creatingQueueCounter : undefined}
            bundlePriceFor5={mod.id === 'queue' ? queueBundle5Price : undefined}
            moduleOutlets={mod.id !== 'queue' ? allOutlets
              .filter(o => isDedicatedToModule(o, mod.id))
              .map(o => ({
                id: o.id,
                name: o.name,
                locked: o.module_locked?.[mod.id] ?? !(o.modules || []).includes(mod.id),
                billedSince: o.module_billed_since?.[mod.id] ?? null,
                expires: o.module_expires?.[mod.id] ?? null,
              })) : undefined}
            pricePerLockedOutlet={mod.id !== 'queue' ? lockedOutletPrices[mod.id] : undefined}
            onReactivateOutlets={onReactivateOutlets}
            reactivating={reactivatingModuleId === mod.id}
            onManageOutlets={onManageOutlets}
          />
        ))}
      </PlanCarousel>
    </div>
  );
}

// ── Root component ──────────────────────────────────────────────────────────
interface SubscriptionPlansViewProps {
  currentPlan?: PlanId;
  onSelectPlan?: (plan: PlanId, cycle: BillingCycle) => void;
  onClose?: () => void;
  embedded?: boolean;
  currentUserData?: any;
  forcedReason?: string;
  onSilentRefresh?: () => void;
}

const EMPTY_STATUSES: Record<ModuleId, ModuleStatus> = {
  ledger: 'inactive', employees: 'inactive', queue: 'inactive', gym: 'inactive', canteen: 'inactive', pos: 'inactive',
};

export default function SubscriptionPlansView({
  currentPlan = 'basic',
  onSelectPlan,
  onClose,
  embedded = false,
  currentUserData,
  forcedReason,
  onSilentRefresh,
}: SubscriptionPlansViewProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [activeTab, setActiveTab]           = useState<'suite' | 'modules'>('suite');
  const cycle: BillingCycle                 = 'monthly'; // Fixed to monthly only
  const [PRICES, setPRICES]                 = useState(DEFAULT_PRICES);
  const [currency, setCurrency] = useState('');
  const [detectedCountry, setDetectedCountry] = useState('');
  const [pricesLoading, setPricesLoading] = useState(true);
  const [moduleStatuses, setModuleStatuses] = useState<Record<ModuleId, ModuleStatus>>(EMPTY_STATUSES);
  const [trialEndDates, setTrialEndDates] = useState<Record<string, string>>({});
  const [loadingModuleId, setLoadingModuleId] = useState<ModuleId | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [moduleDefs, setModuleDefs] = useState<ModuleDef[]>(MODULE_DEFS.map(m => ({ ...m })));
  const [outletCounts, setOutletCounts] = useState<Record<ModuleId, number>>({
    ledger: 1, employees: 1, queue: 1, gym: 1, canteen: 1, pos: 1,
  });
  const [activeOutletCounts, setActiveOutletCounts] = useState<Record<ModuleId, number>>({
    ledger: 1, employees: 1, queue: 1, gym: 1, canteen: 1, pos: 1,
  });
  const [pendingOutletCounts, setPendingOutletCounts] = useState<Record<ModuleId, number | undefined>>({
    ledger: undefined, employees: undefined, queue: undefined, gym: undefined, canteen: undefined, pos: undefined,
  });
  const [seatCounts, setSeatCounts] = useState<Record<ModuleId, number>>({
    ledger: 0, employees: 0, queue: 0, gym: 0, canteen: 0, pos: 0,
  });
  const [pendingSeatCounts, setPendingSeatCounts] = useState<Record<ModuleId, number | undefined>>({
    ledger: undefined, employees: undefined, queue: undefined, gym: undefined, canteen: undefined, pos: undefined,
  });
  const [seatsIncluded, setSeatsIncluded] = useState<Record<ModuleId, number>>({
    ledger: 2, employees: 2, queue: 2, gym: 2, canteen: 2, pos: 2,
  });
  const [seatAddonPrice, setSeatAddonPrice] = useState(0);
  const [queueBundle5Price, setQueueBundle5Price] = useState<number | null>(null);
  const [suiteSeatCount, setSuiteSeatCount] = useState(0);
  const [suitePendingSeatCount, setSuitePendingSeatCount] = useState<number | undefined>(undefined);
  const [suiteSeatsIncluded, setSuiteSeatsIncluded] = useState(1);
  const [suiteSeatAddonPrice, setSuiteSeatAddonPrice] = useState(0);
  const [freeSlotUsed, setFreeSlotUsed] = useState<Record<string, boolean>>({});
  const hadTrial   = currentUserData?.had_trial   || false;
  const planStatus = currentUserData?.plan_status || 'free';

  // ── Queue per-counter billing ────────────────────────────────────────────
  const [queueCounters, setQueueCounters] = useState<{
    id: number; code: string; name: string; outlet_id: number | null; locked: boolean;
  }[]>([]);
  const [selectedQueueOutletId, setSelectedQueueOutletId] = useState<number | null>(null);
  const [selectedQueueCounterIds, setSelectedQueueCounterIds] = useState<number[]>([]);
  const [purchasingQueueCounters, setPurchasingQueueCounters] = useState(false);
  const [creatingQueueOutlet, setCreatingQueueOutlet] = useState(false);
  const [creatingQueueCounter, setCreatingQueueCounter] = useState(false);

  // ── Outlet lock/selection state ─────────────────────────────────────────
  const [allOutlets, setAllOutlets] = useState<OutletRow[]>([]);
  const [selectionModalModuleId, setSelectionModalModuleId] = useState<ModuleId | null>(null);

  const [lockedOutletsByModule, setLockedOutletsByModule] = useState<Record<string, LockedOutlet[]>>({});
  const [lockedOutletPrices, setLockedOutletPrices] = useState<Record<string, number>>({});
  const [reactivatingModuleId, setReactivatingModuleId] = useState<ModuleId | null>(null);

  const handleCreateQueueOutlet = async (name: string) => {
    const trimmed = name.trim();
    if (allOutlets.some(o => o.name.trim().toLowerCase() === trimmed.toLowerCase())) {
      alert(`An outlet named "${trimmed}" already exists in your organization. Choose a different name.`);
      return;
    }
    setCreatingQueueOutlet(true);
    try {
      const res = await fetch(`${BASE}/outlets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name,
          country: detectedCountry,
          currency,
          invoice_prefix: '',
          modules: ['queue'],
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.id) throw new Error(data?.detail || 'Failed to create outlet');
      await refreshOutlets();
      setSelectedQueueOutletId(data.id);
      onSilentRefresh?.();
    } catch (e: any) {
      alert(e.message || 'Could not create outlet. Try again.');
    } finally {
      setCreatingQueueOutlet(false);
    }
  };

  const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
  const token = localStorage.getItem('cloudairambo_jwt') || '';

  const loadRazorpayScript = () =>
    new Promise<boolean>((resolve) => {
      if ((window as any).Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  // Returns true only once verify-topup-payment has actually confirmed the
  // charge, false if the script failed to load, the modal was dismissed, or
  // verification failed. Callers await this to safely sequence multiple
  // top-ups (e.g. outlet then seats) without opening two Razorpay popups
  // at once.
    const openRazorpayTopup = (opts: {
    orderId: string; amount: number; currency: string; keyId: string;
    planId: string; kind: 'outlet' | 'seat' | 'combined'; onSuccess: () => Promise<void>;
  }): Promise<boolean> => {
    return new Promise((resolve) => {
      loadRazorpayScript().then((loaded) => {
        if (!loaded || !(window as any).Razorpay) {
          alert('Could not load payment window. Please try again.');
          resolve(false);
          return;
        }
        const rzp = new (window as any).Razorpay({
          key: opts.keyId,
          order_id: opts.orderId,
          amount: Math.round(opts.amount * 100),
          currency: opts.currency,
          name: 'CloudAIRambo',
          description: `${opts.kind === 'outlet' ? 'Outlet' : opts.kind === 'seat' ? 'Seat' : 'Outlet + Seat'} top-up — ${opts.planId}`,
          handler: async (response: any) => {
            try {
              const res = await fetch(`${BASE}/billing/verify-topup-payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  plan_id: opts.planId,
                  kind: opts.kind,
                }),
              });
              const data = await res.json();
              if (!res.ok) { alert(data.detail || 'Payment succeeded but activation failed — contact support.'); resolve(false); return; }
              await opts.onSuccess();
              resolve(true);
            } catch (e: any) {
              alert('Payment succeeded but activation failed: ' + e.message + ' — contact support.');
              resolve(false);
            }
          },
          modal: { ondismiss: () => resolve(false) },
        });
        rzp.open();
      });
    });
  };

  // Fetch real module statuses from backend
  const refreshModuleStatuses = useCallback(async () => {
    try {
      const res  = await fetch(`${BASE}/billing/modules/status?country=${detectedCountry}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.statuses) setModuleStatuses(data.statuses);
      if (data?.trial_end_dates) setTrialEndDates(data.trial_end_dates);
      if (data?.outlet_counts) setActiveOutletCounts(data.outlet_counts);
      if (data?.pending_outlet_counts) {
        setPendingOutletCounts(prev => ({
          ledger: undefined, employees: undefined, queue: undefined, gym: undefined, canteen: undefined, pos: undefined,
          ...data.pending_outlet_counts,
        }));
      }
      if (data?.seat_counts) setSeatCounts(prev => ({ ...prev, ...data.seat_counts }));
      if (data?.seats_included) setSeatsIncluded(prev => ({ ...prev, ...data.seats_included }));
      if (data?.seat_addon_price) setSeatAddonPrice(data.seat_addon_price);
      if (data?.free_slot_used) setFreeSlotUsed(data.free_slot_used);
      if (data?.pending_seat_counts) {
        setPendingSeatCounts(prev => ({
          ledger: undefined, employees: undefined, queue: undefined, gym: undefined, canteen: undefined, pos: undefined,
          ...data.pending_seat_counts,
        }));
      }
      if (data?.suite) {
        setSuiteSeatCount(data.suite.seat_count ?? 0);
        setSuitePendingSeatCount(data.suite.pending_seat_count ?? undefined);
        setSuiteSeatsIncluded(data.suite.seats_included ?? 1);
        setSuiteSeatAddonPrice(data.suite.addon_price_per_seat ?? 30);
      } else {
        setSuiteSeatCount(0);
        setSuitePendingSeatCount(undefined);
        setSuiteSeatsIncluded(1);
        setSuiteSeatAddonPrice(30);
      }
    } catch (_) {
      // keep last-known state on error
    } finally {
      setInitialLoading(false);
    }
  }, [BASE, token, detectedCountry]);

  useEffect(() => {
    refreshModuleStatuses();
  }, [activeTab, refreshModuleStatuses]);

  const refreshQueueCounters = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/queue/counters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (Array.isArray(data?.counters)) {
        setQueueCounters(data.counters);
      }
    } catch (_) {}
  }, [BASE, token]);

  useEffect(() => {
    refreshQueueCounters();
  }, [refreshQueueCounters]);

  // Default the outlet dropdown to the first outlet that has Queue enabled,
  // once outlets have loaded and nothing's selected yet.
  useEffect(() => {
    if (selectedQueueOutletId == null) {
      const first = allOutlets.find(o => (o.modules || []).includes('queue'));
      if (first) setSelectedQueueOutletId(first.id);
    }
  }, [allOutlets, selectedQueueOutletId]);

  const handleCreateQueueCounter = async (outletId: number, name: string) => {
    const trimmed = name.trim();
    // Client-side duplicate guard — mirrors the backend check, catches it
    // instantly without a round trip. Backend still has the authoritative
    // check for races. Scoped to this outlet only — same name on a
    // different outlet is fine.
    const dup = queueCounters.find(
      c => c.outlet_id === outletId && c.name.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (dup) {
      alert(`A counter named "${dup.name}" already exists on this outlet. Choose a different name.`);
      return;
    }

    setCreatingQueueCounter(true);
    try {
      const res = await fetch(`${BASE}/queue/counters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: trimmed, outlet_id: outletId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to create counter');
      await refreshQueueCounters();
    } catch (e: any) {
      // Surfaces the backend's real reason (duplicate name, cap reached, etc.)
      alert(e.message || 'Could not create counter. Try again.');
    } finally {
      setCreatingQueueCounter(false);
    }
  };

  const handleToggleQueueCounter = (counterId: number) => {
    setSelectedQueueCounterIds(prev =>
      prev.includes(counterId) ? prev.filter(id => id !== counterId) : [...prev, counterId]
    );
  };

  // ── Outlets — powers the "pick your active outlets" forced picker ───────
  const refreshOutlets = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/outlets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: OutletRow[] = await res.json();
      if (!res.ok) return;
      setAllOutlets(data);
      const flagged = data.find(o => o.needs_outlet_selection);
      if (flagged) {
        const modId = (flagged.modules || []).find(
          m => flagged.module_caps && m in flagged.module_caps
        ) as ModuleId | undefined;
        
        // If cap <= 0 (plan expired/unpaid), DO NOT open the modal — allow the user to view plans & renew!
        const cap = modId && flagged.module_caps ? (flagged.module_caps[modId] ?? 0) : 0;
        if (modId && cap > 0) {
          setSelectionModalModuleId(modId);
        } else {
          setSelectionModalModuleId(null);
        }
      }
    } catch (_) {
      // keep last-known state on error
    }
  }, [BASE, token]);

  useEffect(() => {
    refreshOutlets();
  }, [refreshOutlets]);

  const refreshLockedOutlets = useCallback(async () => {
    const modules: ModuleId[] = ['ledger', 'employees', 'gym', 'canteen', 'pos'];
    try {
      const results = await Promise.all(modules.map(async (m) => {
        const res = await fetch(`${BASE}/billing/modules/${m}/locked-outlets?country=${detectedCountry}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return res.json();
      }));
      const byModule: Record<string, LockedOutlet[]> = {};
      const prices: Record<string, number> = {};
      results.forEach((res, i) => {
        byModule[modules[i]] = res.locked_outlets || [];
        if (res.price_per_outlet != null) prices[modules[i]] = res.price_per_outlet;
      });
      setLockedOutletsByModule(byModule);
      setLockedOutletPrices(prices);
    } catch (_) {
      // keep last-known state on error
    }
  }, [BASE, token, detectedCountry]);

  useEffect(() => {
    if (activeTab === 'modules') refreshLockedOutlets();
  }, [activeTab, refreshLockedOutlets]);

  const handleConfirmOutletSelection = async (outletIds: number[]) => {
    if (!selectionModalModuleId) return;
    try {
      const res = await fetch(`${BASE}/outlets/select-active-outlets/${selectionModalModuleId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ outlet_ids: outletIds }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || 'Could not save selection. Please try again.');
        console.error('[select-active-outlets] failed:', res.status, data);
        return;
      }
      setSelectionModalModuleId(null);
      await refreshOutlets();
      await refreshModuleStatuses();
      onSilentRefresh?.();
    } catch (e: any) {
      alert('Network error while saving your selection: ' + (e?.message || 'unknown error'));
      console.error('[select-active-outlets] network error:', e);
    }
  };

  const handleSelectQueueOutlet = (outletId: number) => {
    setSelectedQueueOutletId(outletId);
    setSelectedQueueCounterIds([]); // clear selection when switching outlets
  };

  const handlePurchaseQueueCounters = async () => {
    if (selectedQueueCounterIds.length === 0) return;
    setPurchasingQueueCounters(true);
    try {
      const res = await fetch(`${BASE}/billing/modules/queue/counter-billing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          counter_ids: selectedQueueCounterIds,
          country: detectedCountry,
        }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Could not start counter purchase.'); return; }
      if (!data.checkout_url) { alert('Payment setup failed — please contact support.'); return; }
      window.location.href = data.checkout_url;
    } catch (e: any) {
      alert('Error: ' + e.message);
    } finally {
      setPurchasingQueueCounters(false);
    }
  };

  const handleRequestOutletChange = async (moduleId: ModuleId, count: number): Promise<boolean> => {
    try {
      const res = await fetch(`${BASE}/billing/modules/${moduleId}/request-outlet-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ outlet_count: count, country: detectedCountry }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Could not update outlet count.'); return false; }

      if (data.order_required) {
        return await openRazorpayTopup({
          orderId: data.order_id, amount: data.amount, currency: data.currency,
          keyId: data.razorpay_key_id, planId: moduleId, kind: 'outlet',
          onSuccess: async () => {
            await refreshModuleStatuses();
            onSilentRefresh?.();
          },
        });
      }

      await refreshModuleStatuses();
      onSilentRefresh?.();
      return true;
    } catch (e: any) {
      alert('Error: ' + e.message);
      return false;
    }
  };

  const handleReactivateOutlets = async (moduleId: ModuleId, outletIds: number[]) => {
    if (outletIds.length === 0) return;
    setReactivatingModuleId(moduleId);
    try {
      // One payment mandate covers everything selected today — each
      // outlet still gets its own independent 30-day clock once confirmed,
      // so reactivating 1 or several at once both work the same way.
      const res = await fetch(`${BASE}/billing/modules/${moduleId}/outlet-billing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ outlet_ids: outletIds, country: detectedCountry }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Could not reactivate outlets.'); return; }
      if (data.free) {
        await refreshOutlets();
        await refreshLockedOutlets();
        await refreshModuleStatuses();
        onSilentRefresh?.();
        alert('✅ Outlets reactivated — it\'s free!');
        return;
      }
      if (!data.checkout_url) { alert('Payment setup failed — please contact support.'); return; }
      window.location.href = data.checkout_url;
    } catch (e: any) {
      alert('Error: ' + e.message);
    } finally {
      setReactivatingModuleId(null);
    }
  };

  useEffect(() => {
    const fit = () => {
      if (!contentRef.current) return;
      const contentHeight = contentRef.current.scrollHeight;
      const available = window.innerHeight * 0.98; // use almost full viewport height
      const rawScale = contentHeight > available ? available / contentHeight : 1;
      setScale(Math.max(rawScale, 0.85)); // never shrink below 85% — stay readable
    };
    fit();
    window.addEventListener('resize', fit);
    const ro = new ResizeObserver(fit);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => { window.removeEventListener('resize', fit); ro.disconnect(); };
  }, [activeTab]);

  const handleRequestSeatChange = async (moduleId: ModuleId, count: number): Promise<boolean> => {
    try {
      const res = await fetch(`${BASE}/billing/${moduleId}/request-seat-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ seat_count: count, country: detectedCountry }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Could not update seat count.'); return false; }

      if (data.order_required) {
        return await openRazorpayTopup({
          orderId: data.order_id, amount: data.amount, currency: data.currency,
          keyId: data.razorpay_key_id, planId: moduleId, kind: 'seat',
          onSuccess: async () => {
            await refreshModuleStatuses();
            onSilentRefresh?.();
          },
        });
      }

      await refreshModuleStatuses();
      onSilentRefresh?.();
      return true;
    } catch (e: any) {
      alert('Error: ' + e.message);
      return false;
    }
  };

  const handleRequestCombinedChange = async (moduleId: ModuleId, outletCount: number, seatCount: number): Promise<boolean> => {
    try {
      const res = await fetch(`${BASE}/billing/modules/${moduleId}/request-combined-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ outlet_count: outletCount, seat_count: seatCount, country: detectedCountry }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Could not update outlets/seats.'); return false; }

      if (data.order_required) {
        const ok = await openRazorpayTopup({
          orderId: data.order_id, amount: data.amount, currency: data.currency,
          keyId: data.razorpay_key_id, planId: moduleId, kind: 'combined',
          onSuccess: async () => {
            await refreshModuleStatuses();
            onSilentRefresh?.();
          },
        });
        if (ok) alert('✅ Changes confirmed!');
        return ok;
      }

      await refreshModuleStatuses();
      onSilentRefresh?.();
      return true;
    } catch (e: any) {
      alert('Error: ' + e.message);
      return false;
    }
  };

  const handleRequestSuiteSeatChange = async (planId: PlanId, count: number) => {
    try {
      const res = await fetch(`${BASE}/billing/${planId}/request-seat-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ seat_count: count, country: detectedCountry }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Could not update seat count.'); return; }
      setSuitePendingSeatCount(data.pending_extra_seats ?? undefined);
    } catch (e: any) {
      alert('Error: ' + e.message);
    }
  };

  // Start trial — no card, no Cashfree
  const handleStartTrial = async (moduleId: ModuleId) => {
    setLoadingModuleId(moduleId);
    try {
      const res  = await fetch(`${BASE}/billing/modules/${moduleId}/start-trial`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.statuses) setModuleStatuses(data.statuses);
      onSilentRefresh?.();
    } catch (e: any) {
      alert('Could not start trial: ' + e.message);
    } finally {
      setLoadingModuleId(null);
    }
  };

  // Subscribe (trial → pay, or locked → pay) — goes through Razorpay checkout.
  // Each module is priced and billed independently — no bundle discount.
  const handleModuleSubscribe = async (moduleId: ModuleId, forceSwitch: boolean = false) => {
    setLoadingModuleId(moduleId);
    try {
      const res = await fetch(`${BASE}/billing/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: moduleId, tier: 'flat', billing_cycle: 'monthly', country: detectedCountry, force_switch: forceSwitch, outlet_count: outletCounts[moduleId] }),
      });
      const data = await res.json();
      if (res.status === 400 && data.detail?.includes('Full Suite')) {
        const confirmed = window.confirm(
          `${data.detail}\n\nContinuing will start checkout for this module. Your Full Suite plan stays fully active and untouched until this module's payment is confirmed — only then will Suite be cancelled (no refund for its remaining time). If checkout fails or you abandon it, nothing changes and you keep your Suite plan.\n\nContinue?`
        );
        if (confirmed) {
          return handleModuleSubscribe(moduleId, true);
        }
        return;
      }
      if (!res.ok) { alert(data.detail || 'Could not start subscription.'); return; }
      if (data.free) {
        onSilentRefresh?.();
        alert('Module activated — it\'s free!');
        return;
      }
      if (forceSwitch) onSilentRefresh?.();
      if (!data.checkout_url) { alert('Payment setup failed — please contact support.'); return; }

      window.location.href = data.checkout_url;
    } catch (e: any) {
      alert('Error: ' + e.message);
    } finally {
      setLoadingModuleId(null);
    }
  };

  // Individual Suite paid flow
  const handleSelectPaid = async (plan: string) => {
    try {
      const res = await fetch(`${BASE}/billing/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan, billing_cycle: cycle, country: detectedCountry }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || 'Could not start subscription.'); return; }
      if (!data.checkout_url) { alert('Payment setup failed — please contact support.'); return; }

      window.location.href = data.checkout_url;
    } catch (e: any) {
      alert('Error: ' + e.message);
    }
  };

  // Fetch country-based prices on mount
  useEffect(() => {
    fetch(`${BASE}/billing/my-country`)
      .then(r => r.json())
      .then(data => {
        // Detect India by timezone if server didn't detect IP
        const tz = (Intl && Intl.DateTimeFormat) ? Intl.DateTimeFormat().resolvedOptions().timeZone : '';
        const tzCountry = (tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta') ? 'IN' : '';
        const country = data.country || tzCountry || '';

        setDetectedCountry(country);
        return fetch(`${BASE}/billing/prices${country ? `?country=${country}` : ''}`)
          .then(r => r.json())
          .then((rows: { plan: string; billing_cycle: string; price: number; currency: string; country: string | null }[]) => {
            if (Array.isArray(rows) && rows.length > 0) {
              const countryRow = rows.find(r => r.country && r.country === country);
              const fallbackRow = rows.find(r => !r.country);
              const activeCurrency = countryRow?.currency || fallbackRow?.currency || rows[0]?.currency || '';
              setCurrency(activeCurrency);
            }

            const p = JSON.parse(JSON.stringify(DEFAULT_PRICES));
            const updatedModules = MODULE_DEFS.map(m => ({ ...m }));
            const seenSuiteKeys = new Set<string>();
            const seenModuleKeys = new Set<string>();

            rows.forEach(r => {
              if (p[r.plan as PlanId]) {
                const suiteKey = `${r.plan}:${r.billing_cycle}`;
                if (seenSuiteKeys.has(suiteKey)) {
                  console.warn(`[pricing] duplicate row for ${suiteKey} — ignoring the extra one.`);
                  return;
                }
                seenSuiteKeys.add(suiteKey);

                const cycleKey = r.billing_cycle as BillingCycle;
                const currencyKey = cycleKey === 'monthly' ? 'monthlyCurrency' : 'yearlyCurrency';
                p[r.plan as PlanId][cycleKey] = r.price;
                p[r.plan as PlanId][currencyKey] = r.currency;
                return;
              }

              if (r.plan === 'qbundle5' && r.billing_cycle === 'monthly') {
                setQueueBundle5Price(r.price);
                return;
              }

              const modIdx = updatedModules.findIndex(m => m.id === r.plan);
              if (modIdx !== -1 && r.billing_cycle === 'monthly') {
                const modKey = `${r.plan}:${r.billing_cycle}`;
                if (seenModuleKeys.has(modKey)) {
                  console.warn(`[pricing] duplicate row for ${modKey} — ignoring the extra one.`);
                  return;
                }
                seenModuleKeys.add(modKey);

                updatedModules[modIdx].monthlyPrice = r.price;
                updatedModules[modIdx].monthlyCurrency = r.currency;
              }
            });

            setPRICES(p);
            setModuleDefs(updatedModules);
          })
          .finally(() => {
            setPricesLoading(false);
          });
      })
      .catch(() => {
        setPricesLoading(false);
      });
  }, [BASE]);

  const handleSelect = (plan: PlanId) => {
    if (plan === currentPlan) return;
    onSelectPlan?.(plan, cycle);
  };

  const hasActiveModules = Object.values(moduleStatuses).some(s => s === 'active' || s === 'trial');

  const content = (
    <>
    {selectionModalModuleId && (() => {
      const modId = selectionModalModuleId;
      const modDef = moduleDefs.find(m => m.id === modId);
      const dedicated = allOutlets.filter(o => isDedicatedToModule(o, modId));
      const outletsForModule = dedicated.length > 0 ? dedicated : allOutlets;
      const cap = outletsForModule.find(o => o.module_caps)?.module_caps?.[modId] ?? activeOutletCounts[modId] ?? 1;
      const currentActiveIds = outletsForModule.filter(o => (o.modules || []).includes(modId) && !o.locked && !o.module_locked?.[modId]).map(o => o.id);
      return (
        <OutletSelectionModal
          moduleName={modDef?.name || modId}
          outlets={outletsForModule.map(o => ({ id: o.id, name: o.name }))}
          cap={cap}
          currentActiveIds={currentActiveIds}
          onConfirm={handleConfirmOutletSelection}
          onClose={() => setSelectionModalModuleId(null)}
        />
      );
    })()}
    <style>{`
  @media (max-width: 768px) {
    .spv-suite-grid { grid-template-columns: 1fr !important; }
    .spv-modules-grid { grid-template-columns: 1fr !important; }
  }
  @media (max-width: 1024px) and (min-width: 769px) {
    .spv-suite-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .spv-modules-grid { grid-template-columns: repeat(2, 1fr) !important; }
  }
  .spv-modal-scroll {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .spv-modal-scroll::-webkit-scrollbar {
    display: none;
  }
  .spv-carousel-scroll {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .spv-carousel-scroll::-webkit-scrollbar {
    display: none;
  }
`}</style>
    <div style={{ maxWidth: 980, margin: '0 auto', padding: embedded ? '0' : '40px 24px', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        {!embedded && onClose && (
          <button onClick={onClose} style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer' }}>✕</button>
        )}
        <div style={{ display: 'inline-block', background: '#eef2ff', color: '#6366f1', fontSize: 11, fontWeight: 800, padding: '4px 14px', borderRadius: 99, letterSpacing: '0.06em', marginBottom: 14, textTransform: 'uppercase' }}>
          CloudAIRambo Plans
        </div>
        <h2 style={{ fontSize: 28, fontWeight: 900, color: '#0f172a', margin: '0 0 8px' }}>
          Pick the right plan for your business
        </h2>
        <p style={{ fontSize: 14, color: '#64748b', margin: 0 }}>
          All plans include invoicing, PDF export, and client management. Upgrade anytime.
        </p>
        {forcedReason && (
          <div style={{
            marginTop: 18,
            display: 'inline-block',
            background: 'linear-gradient(135deg,#eef2ff,#f5f3ff)',
            border: '1.5px solid #c7d2fe',
            borderRadius: 14,
            padding: '12px 22px',
            fontSize: 13,
            fontWeight: 700,
            color: '#4338ca',
            maxWidth: 520,
          }}>
            {forcedReason}
          </div>
        )}
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 36 }}>
        <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 12, padding: 4, gap: 2 }}>
          {([{ key: 'suite', label: '👤 Individual' }, { key: 'modules', label: '🧩 Module Plans' }] as { key: 'suite' | 'modules'; label: string }[]).map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: '8px 22px', borderRadius: 9, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 700,
              background: activeTab === tab.key ? '#fff' : 'transparent',
              color: activeTab === tab.key ? '#0f172a' : '#64748b',
              boxShadow: activeTab === tab.key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.15s',
            }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Individual Suite tab */}
      {activeTab === 'suite' && (
        pricesLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6366f1', fontWeight: 700, fontSize: 14 }}>
            Loading plans...
          </div>
        ) : (
        <>
          {/* Monthly pricing note */}
          <div style={{ textAlign: 'center', marginBottom: 24, fontSize: 12, fontWeight: 700, color: '#6366f1' }}>
            Billed monthly · Cancel or upgrade anytime
          </div>

          <PlanCarousel initialIndex={1}>
            {(['basic', 'pro', 'ultra'] as PlanId[]).map(p => (
              <PlanCard
                key={p} planId={p} cycle={cycle}
                currentPlan={currentPlan}
                onSelect={handleSelect}
                onSelectPaid={plan => handleSelectPaid(plan)}
                prices={PRICES} currency={currency}
                hadTrial={hadTrial} planStatus={planStatus}
                currentUserData={currentUserData}
                hasActiveModules={hasActiveModules && !(currentPlan === 'pro' || currentPlan === 'ultra')}
                seatCount={suiteSeatCount}
                pendingSeatCount={suitePendingSeatCount}
                seatsIncluded={suiteSeatsIncluded}
                seatAddonPrice={suiteSeatAddonPrice}
                onRequestSeatChange={handleRequestSuiteSeatChange}
              />
            ))}
          </PlanCarousel>

          <ComparisonTable />
        </>
        )
      )}

      {/* Module Plans tab */}
      {activeTab === 'modules' && (
        <>
          {(pricesLoading || initialLoading) ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#6366f1', fontWeight: 700, fontSize: 14 }}>
              Loading your module plans...
            </div>
          ) : (
          <ModulePlansTab
            currency={currency}
            queueBundle5Price={queueBundle5Price}
            modules={moduleDefs}
            statuses={moduleStatuses}
            loadingModuleId={loadingModuleId}
            anyModuleInTrial={hadTrial || hasActiveModules}
            freeSlotUsed={freeSlotUsed}
            trialEndDates={trialEndDates}
            onStartTrial={handleStartTrial}
            onSubscribe={handleModuleSubscribe}
            outletCounts={outletCounts}
            onOutletCountChange={(id, count) => setOutletCounts(prev => ({ ...prev, [id]: count }))}
            activeOutletCounts={activeOutletCounts}
            pendingOutletCounts={pendingOutletCounts}
            onRequestOutletChange={handleRequestOutletChange}
            seatCounts={seatCounts}
            pendingSeatCounts={pendingSeatCounts}
            seatsIncluded={seatsIncluded}
            seatAddonPrice={seatAddonPrice}
            onRequestSeatChange={handleRequestSeatChange}
            onRequestCombinedChange={handleRequestCombinedChange}
            queueOutlets={allOutlets.filter(o => (o.modules || []).includes('queue')).map(o => ({ id: o.id, name: o.name }))}
            queueCounters={queueCounters}
            selectedQueueOutletId={selectedQueueOutletId}
            onSelectQueueOutlet={handleSelectQueueOutlet}
            selectedQueueCounterIds={selectedQueueCounterIds}
            onToggleQueueCounter={handleToggleQueueCounter}
            onPurchaseQueueCounters={handlePurchaseQueueCounters}
            purchasingQueueCounters={purchasingQueueCounters}
            onCreateQueueOutlet={handleCreateQueueOutlet}
            creatingQueueOutlet={creatingQueueOutlet}
            onCreateQueueCounter={handleCreateQueueCounter}
            creatingQueueCounter={creatingQueueCounter}
            allOutlets={allOutlets}
            lockedOutletPrices={lockedOutletPrices}
            onReactivateOutlets={handleReactivateOutlets}
            reactivatingModuleId={reactivatingModuleId}
            onManageOutlets={(modId) => setSelectionModalModuleId(modId)}
          />
          )}
        </>
      )}

      <p style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 40, lineHeight: 1.6 }}>
        Prices shown in local currency where available · Individual Basic: free forever · Module plans: 7-day free trial, no card needed · Cancel anytime.<br />
        Need a custom deal? <a href="mailto:support@cloudairambo.com" style={{ color: '#6366f1', fontWeight: 700 }}>Contact us</a>
      </p>
    </div>
    </>
  );

  if (embedded) return content;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    }}>
      <div className="spv-modal-scroll" style={{
        background: '#fff', borderRadius: 24, width: '100%', maxWidth: 1020,
        maxHeight: '99vh', position: 'relative', boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
        overflowY: 'auto', overflowX: 'hidden',
      }}>
        <div ref={contentRef} style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}>
          {content}
        </div>
      </div>
    </div>
  );
}
